// API utility functions for the task management system
import { authStore } from '@/store/auth'
import { request } from '@/lib/http'
import { runAuthenticatedDownload } from '@/lib/downloads'
import { tasksApi } from '@/features/tasks/api'
import { projectsApi } from '@/features/projects/api'
import { scheduleApi } from '@/features/schedule/api'
import { hrApi } from '@/features/hr/api'
import { financeApi } from '@/features/finance/api'

export * from '@/types'
// `export * from` re-exports for consumers but does not bind names locally —
// the ApiClient method signatures below reference these types directly, so
// they need their own (type-only, erased at build) import too.
import type {
  User, LoginResponse, StatusCategory, ProjectStatus, Label, Task, ApprovalTask,
  TaskComment, ChecklistItem, TaskDependencies, TaskDependency, RecurringTask,
  TaskTemplate, ActivityItem, DailyPlanBlock, TaskReminder, Team,
  ProjectMember, AppNotification, WorkloadItem, StatusReportTask, ProjectStatusReport,
  Project, ProjectTemplate, ProjectPhase, DashboardMetrics, Schedule, ScheduleParticipant,
  ApprovalStatus, ApprovalDecision, LeaveRequest, ExpenseRequest, TaskAttachment, OrgMember,
} from '@/types'

// API Client Class
export class ApiClient {
  // Auth methods
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    })
    
    // Store token and user in authStore
    authStore.getState().setAccessToken(response.access_token)
    authStore.getState().setUser(response.user)
    return response
  }

  async register(email: string, password: string): Promise<{ message: string }> {
    return request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    })
  }

  async logout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' })
    } finally {
      authStore.getState().logout()
    }
  }

  async getCurrentUser(): Promise<{ user: User }> {
    return request('/auth/me')
  }

  // ── Identity (org members) — /api/orgs/current/members ──────────────────────
  async listOrgMembers(): Promise<{ items: OrgMember[]; total: number }> {
    return request('/orgs/current/members')
  }

  // ── Org-scoped projects (picker + Projects pages) — /api/work/projects ──────
  async listWorkProjects(params?: { status?: 'active' | 'archived' }): Promise<{ items: Project[]; total: number }> {
    return projectsApi.list(params)
  }

  async getWorkProject(id: string): Promise<Project> {
    return projectsApi.get(id)
  }

  async createWorkProject(data: {
    name: string
    description?: string
    intake_answers?: Record<string, string>
    start_date?: string
    due_date?: string
    team_id?: number
    team_ids?: number[]
    member_ids?: number[]
  }): Promise<Project> {
    return projectsApi.create(data)
  }

  async updateWorkProject(id: string, data: Partial<Pick<Project, 'name' | 'description' | 'intake_answers' | 'status' | 'start_date' | 'due_date' | 'is_template'> & { member_ids?: number[] }>): Promise<Project> {
    return projectsApi.update(id, data)
  }

  async archiveWorkProject(id: string): Promise<Project> {
    return projectsApi.archive(id)
  }

  async listProjectMembers(projectId: number): Promise<{ items: ProjectMember[] }> {
    return projectsApi.listMembers(projectId)
  }

  async addProjectMember(projectId: number, data: { user_id: number; role?: 'member' | 'project_manager' }): Promise<ProjectMember> {
    return request(`/work/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify(data) })
  }

  async removeProjectMember(projectId: number, userId: number, scope: 'project' | 'team'): Promise<{ ok: boolean; scope: string }> {
    return request(`/work/projects/${projectId}/members/${userId}?scope=${scope}`, { method: 'DELETE' })
  }

  async setProjectMemberRole(projectId: number, userId: number, role: 'member' | 'project_manager'): Promise<ProjectMember> {
    return request(`/work/projects/${projectId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) })
  }

  async listProjectTeams(projectId: number): Promise<{ items: Team[] }> {
    return request(`/work/projects/${projectId}/teams`)
  }

  /** All org teams (org-scoped) — unlike getTeams() which is membership-joined. */
  async listOrgTeams(): Promise<{ teams: Team[] }> {
    return request('/work/teams')
  }

  async addProjectTeam(projectId: number, teamId: number): Promise<{ id: number; project_id: number; team_id: number }> {
    return request(`/work/projects/${projectId}/teams`, { method: 'POST', body: JSON.stringify({ team_id: teamId }) })
  }

  async removeProjectTeam(projectId: number, teamId: number): Promise<{ ok: boolean }> {
    return request(`/work/projects/${projectId}/teams/${teamId}`, { method: 'DELETE' })
  }

  async listNotifications(): Promise<{ items: AppNotification[]; unread_count: number; total: number }> {
    return request('/me/notifications')
  }

  async markNotificationRead(id: number): Promise<AppNotification> {
    return request(`/me/notifications/${id}/read`, { method: 'POST' })
  }

  async markAllNotificationsRead(): Promise<{ updated: number }> {
    return request('/me/notifications/read-all', { method: 'POST' })
  }

  async getProjectWorkload(projectId: number): Promise<{ items: WorkloadItem[]; overload_threshold: number }> {
    return request(`/work/projects/${projectId}/workload`)
  }

  async getProjectStatusReport(projectId: number): Promise<ProjectStatusReport> {
    return projectsApi.getStatusReport(projectId)
  }

  async getPortfolio(): Promise<{ items: Array<Project & { task_total: number; task_completed: number; task_in_progress: number; task_overdue: number; progress_pct: number; upcoming_phase: ProjectPhase | null; member_count: number }>; total: number }> {
    return request('/work/portfolio')
  }

  async getOverdueTasks(): Promise<{ items: Task[]; total: number }> {
    return request('/work/overdue')
  }

  // ── Template snapshot ─────────────────────────────────────────────────────
  async snapshotProjectTemplate(projectId: number, name: string): Promise<{ ok: boolean; template_id: number; name: string; task_count: number }> {
    return request(`/work/projects/${projectId}/snapshot-template`, { method: 'POST', body: JSON.stringify({ name }) })
  }

  // ── Project custom statuses — /api/work/projects/:id/statuses ───────────
  async listProjectStatuses(projectId: number): Promise<{ items: ProjectStatus[] }> {
    return request(`/work/projects/${projectId}/statuses`)
  }

  async setProjectStatuses(
    projectId: number,
    statuses: Array<Pick<ProjectStatus, 'key' | 'label' | 'category' | 'color'>>,
  ): Promise<{ items: ProjectStatus[] }> {
    return request(`/work/projects/${projectId}/statuses`, {
      method: 'PUT',
      body: JSON.stringify({ statuses }),
    })
  }

  async deleteProjectStatus(projectId: number, statusId: number): Promise<{ ok: boolean }> {
    return request(`/work/projects/${projectId}/statuses/${statusId}`, { method: 'DELETE' })
  }

  // ── Work domain (org-scoped tasks + approval workflow) — /api/work ──────────
  async listWorkTasks(filters?: {
    assignee?: 'me'
    created_by?: 'me'
    status?: string
    approval?: string
    project_id?: number
    // 1.9.2 search + filters
    q?: string
    priority?: string
    due_before?: string
    due_after?: string
    label?: string
    // 1.9.21 risk flag filter
    flagged?: boolean
  }): Promise<{ items: Task[]; total: number }> {
    return tasksApi.list(filters)
  }

  async getWorkTask(id: string): Promise<Task> {
    return tasksApi.get(id)
  }

  async getTaskActivity(id: number): Promise<{ items: ActivityItem[]; total: number }> {
    return tasksApi.getActivity(id)
  }

  async getProjectActivity(projectId: number): Promise<{ items: ActivityItem[]; total: number }> {
    return request(`/work/projects/${projectId}/activity`)
  }

  async addTaskComment(id: number, body: string, opts?: { parent_comment_id?: number; mentioned_users?: number[] }): Promise<TaskComment> {
    return request(`/work/tasks/${id}/comments`, { method: 'POST', body: JSON.stringify({ body, ...opts }) })
  }

  async listApprovals(
    opts: { kind?: 'validation' | 'completion' | 'all'; limit?: number; offset?: number } = {},
  ): Promise<{ items: ApprovalTask[]; total: number }> {
    const qs = new URLSearchParams()
    if (opts.kind) qs.set('kind', opts.kind)
    if (opts.limit != null) qs.set('limit', String(opts.limit))
    if (opts.offset != null) qs.set('offset', String(opts.offset))
    const suffix = qs.toString() ? `?${qs}` : ''
    return request(`/work/approvals${suffix}`)
  }

  async validateTask(id: number): Promise<Task> {
    return tasksApi.validate(id)
  }

  async declineTask(id: number, reason?: string): Promise<Task> {
    return tasksApi.decline(id, reason)
  }

  async createWorkTask(data: { title: string; priority?: string; project_id?: number; phase_id?: number | null; due_date?: string; assigned_to?: number; description?: string; labels?: string[] }): Promise<Task> {
    return tasksApi.create(data)
  }

  async bulkUpdateTasks(
    ids: number[],
    action: 'assign' | 'status' | 'priority' | 'delete' | 'phase' | 'copy',
    value?: string | number | null,
  ): Promise<{ updated: number; skipped: Array<{ id: number; reason: string }> }> {
    return request('/work/tasks/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action, value }),
    })
  }

  async updateWorkTask(id: string, data: Partial<Pick<Task, 'title' | 'description' | 'priority' | 'status' | 'assigned_to' | 'due_date' | 'labels' | 'project_id' | 'phase_id'>>): Promise<Task> {
    return tasksApi.update(id, data)
  }

  async copyTask(id: number, projectId: number): Promise<Task> {
    return request(`/work/tasks/${id}/copy`, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    })
  }

  async deleteWorkTask(id: string): Promise<{ ok: boolean }> {
    return tasksApi.remove(id)
  }

  async submitTask(id: number): Promise<Task> {
    return request(`/work/tasks/${id}/submit`, { method: 'POST' })
  }

  async approveTask(id: number): Promise<Task> {
    return tasksApi.approve(id)
  }

  async rejectTask(id: number, reason?: string): Promise<Task> {
    return tasksApi.reject(id, reason)
  }

  async flagTask(taskId: number, flag: 'at_risk' | 'blocked' | null, note?: string): Promise<Task> {
    return tasksApi.flag(taskId, flag, note)
  }

  // ── Daily planner — /api/me/daily-plan ──────────────────────────────────
  async getDailyPlan(date?: string): Promise<{ items: DailyPlanBlock[]; date: string; work_start_min: number; work_end_min: number }> {
    const qs = date ? `?date=${date}` : ''
    return request(`/me/daily-plan${qs}`)
  }

  async createPlanBlock(data: { date: string; start_min: number; end_min: number; task_id?: number | null; note?: string }): Promise<DailyPlanBlock> {
    return request('/me/daily-plan', { method: 'POST', body: JSON.stringify(data) })
  }

  async deletePlanBlock(id: number): Promise<{ ok: boolean }> {
    return request(`/me/daily-plan/${id}`, { method: 'DELETE' })
  }

  // ── Time tracking ────────────────────────────────────────────────────────
  async logTime(taskId: number, minutes: number, note?: string): Promise<Task> {
    return tasksApi.logTime(taskId, minutes, note)
  }

  // ── Task reminders — /api/work/reminders ────────────────────────────────
  async listReminders(): Promise<{ items: TaskReminder[]; total: number }> {
    return request('/work/reminders')
  }

  async getTaskReminders(taskId: number): Promise<{ items: TaskReminder[] }> {
    return request(`/work/tasks/${taskId}/reminders`)
  }

  async createTaskReminder(taskId: number, remind_at: string): Promise<TaskReminder> {
    return request(`/work/tasks/${taskId}/reminders`, { method: 'POST', body: JSON.stringify({ remind_at }) })
  }

  async deleteReminder(id: number): Promise<{ ok: boolean }> {
    return request(`/work/reminders/${id}`, { method: 'DELETE' })
  }

  async markReminderFired(id: number): Promise<TaskReminder> {
    return request(`/work/reminders/${id}/fire`, { method: 'PATCH' })
  }

  // ── Web-push endpoints ──────────────────────────────────────────────────────
  async getVapidPublicKey() { return request<{ key: string | null }>('/work/push/vapid-public-key') }
  async subscribePush(sub: PushSubscriptionJSON) {
    return request('/work/push/subscribe', { method: 'POST', body: JSON.stringify(sub) })
  }
  async unsubscribePush(endpoint: string) {
    return request('/work/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) })
  }

  // Dashboard methods
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return request('/insights/dashboard')
  }

  async getTeamActivity(): Promise<any> {
    return request('/insights/team-activity')
  }

  async getPerformanceSummary(): Promise<any> {
    return request('/insights/performance-summary')
  }

  // Invitation lookup/accept — still served by the legacy /api/teams prefix
  // (app/domains/identity/invitations.py), unlike department CRUD which moved
  // to departmentsApi (@/features/teams) — see plan 019.
  async getInvitation(token: string): Promise<{ email: string; team_id: number; team_name: string | null; role: string; expires_at: string | null }> {
    return request(`/teams/invitations/${token}`)
  }

  async acceptInvitation(token: string): Promise<{ message: string; team: Team | null; membership: { team_id: number; role: string } }> {
    return request(`/teams/invitations/${token}/accept`, { method: 'POST' })
  }

  // Schedule methods
  async getSchedules(filters?: {
    start_date?: string
    end_date?: string
    status?: string
    project_id?: number
    team_id?: number
  }): Promise<{ schedules: Schedule[], total: number }> {
    return scheduleApi.list(filters)
  }

  async createSchedule(scheduleData: {
    title: string
    description?: string
    date: string
    start_time: string
    end_time: string
    timezone?: string
    meeting_type?: 'in_person' | 'google_meet' | 'zoom' | 'teams'
    meeting_link?: string
    location?: string
    is_recurring?: boolean
    recurring_pattern?: string
    project_id?: number
    team_id?: number
    participants?: Array<{
      user_id?: number
      email?: string
      name?: string
    }>
  }): Promise<{ message: string, schedule: Schedule }> {
    return scheduleApi.create(scheduleData)
  }

  async getSchedule(id: number): Promise<{ schedule: Schedule }> {
    return scheduleApi.get(id)
  }

  async updateSchedule(id: number, scheduleData: Partial<Schedule>): Promise<{ message: string, schedule: Schedule }> {
    return scheduleApi.update(id, scheduleData)
  }

  async deleteSchedule(id: number): Promise<{ message: string }> {
    return scheduleApi.remove(id)
  }

  async addScheduleParticipant(scheduleId: number, participantData: {
    user_id?: number
    email?: string
    name?: string
  }): Promise<{ message: string, participant: ScheduleParticipant }> {
    return scheduleApi.addParticipant(scheduleId, participantData)
  }

  async respondToSchedule(scheduleId: number, participantId: number, responseStatus: 'accepted' | 'declined' | 'tentative'): Promise<{ message: string, participant: ScheduleParticipant }> {
    return scheduleApi.respond(scheduleId, participantId, responseStatus)
  }

  // User profile methods
  async getMe(): Promise<User> {
    return request('/users/me')
  }

  async updateMe(payload: Partial<Pick<User, 'username' | 'full_name' | 'display_name' | 'avatar_url' | 'plan'>> & { onboarding_complete?: boolean }): Promise<User> {
    return request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  }

  async changePassword(current_password: string, new_password: string): Promise<{ message: string }> {
    return request('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password })
    })
  }

  // Insights / Analytics
  async getInsightsTeamActivity(): Promise<any> { return request('/insights/team-activity') }
  async getInsightsPerformance(): Promise<any> { return request('/insights/performance-summary') }
  async getInsightsTaskStats(): Promise<any> { return request('/insights/task-stats') }
  async getPersonalAnalytics(days: number = 30): Promise<any> {
    return request(`/analytics/personal?days=${days}`)
  }
  async getTeamAnalytics(teamId: number): Promise<any> {
    return request(`/analytics/team/${teamId}`)
  }
  async getProjectAnalytics(projectId: number): Promise<any> {
    return request(`/analytics/project/${projectId}`)
  }

  // Comments
  async getTaskComments(taskId: number): Promise<any> {
    return request(`/comments/task/${taskId}`)
  }
  async createTaskComment(taskId: number, content: string, parentCommentId?: number): Promise<any> {
    return request(`/comments/task/${taskId}`, {
      method: 'POST',
      body: JSON.stringify({ content, parent_comment_id: parentCommentId })
    })
  }
  async deleteComment(commentId: number): Promise<{ message: string }> {
    return request(`/comments/${commentId}`, { method: 'DELETE' })
  }

  // ── Module 1 · HR (leave) ──────────────────────────────
  async getMyLeave(status?: string): Promise<{ leave_requests: LeaveRequest[] }> {
    return hrApi.getMyLeave(status)
  }
  async createLeave(payload: {
    team_id: number; leave_type: string; start_date: string; end_date: string; days_count?: number
    reason?: string; handover_notes: string
  }): Promise<LeaveRequest> {
    return hrApi.createLeave(payload)
  }
  async getTeamLeave(teamId: number, status?: string): Promise<{ leave_requests: LeaveRequest[] }> {
    return hrApi.getTeamLeave(teamId, status)
  }
  async getLeaveApprovals(): Promise<{ leave_requests: LeaveRequest[] }> {
    return hrApi.getLeaveApprovals()
  }
  async decideLeave(id: number, status: ApprovalDecision, notes?: string): Promise<LeaveRequest> {
    return hrApi.decideLeave(id, status, notes)
  }

  // ── Module 1 · Finance (expenses) ──────────────────────
  async getMyExpenses(status?: string): Promise<{ expense_requests: ExpenseRequest[] }> {
    return financeApi.getMyExpenses(status)
  }
  async createExpense(payload: Parameters<typeof financeApi.createExpense>[0]): Promise<ExpenseRequest> {
    return financeApi.createExpense(payload)
  }
  async updateExpense(
    id: number,
    payload: Parameters<typeof financeApi.updateExpense>[1],
    opId?: string,
  ): Promise<ExpenseRequest> {
    return financeApi.updateExpense(id, payload, opId)
  }
  async getTeamExpenses(teamId: number, status?: string): Promise<{ expense_requests: ExpenseRequest[] }> {
    return financeApi.getTeamExpenses(teamId, status)
  }
  async getExpenseApprovals(): Promise<{ expense_requests: ExpenseRequest[] }> {
    return financeApi.getExpenseApprovals()
  }
  async decideExpense(id: number, status: ApprovalDecision, notes?: string): Promise<ExpenseRequest> {
    return financeApi.decideExpense(id, status, notes)
  }

  // ── Task dependencies — /api/work/tasks/:id/dependencies ────────────────
  async getTaskDependencies(taskId: number): Promise<TaskDependencies> {
    return tasksApi.getDependencies(taskId)
  }

  async addTaskDependency(taskId: number, dependsOnId: number): Promise<{ id: number; task_id: number; depends_on_id: number }> {
    return tasksApi.addDependency(taskId, dependsOnId)
  }

  async removeTaskDependency(taskId: number, depId: number): Promise<void> {
    return tasksApi.removeDependency(taskId, depId)
  }

  async listProjectTaskDependencies(projectId: number): Promise<{ items: TaskDependency[] }> {
    return projectsApi.listTaskDependencies(projectId)
  }

  // ── Task checklist — /api/work/tasks/:id/checklist ──────────────────────
  async listChecklist(taskId: number): Promise<{ items: ChecklistItem[] }> {
    return request(`/work/tasks/${taskId}/checklist`)
  }

  async addChecklistItem(taskId: number, text: string): Promise<ChecklistItem> {
    return request(`/work/tasks/${taskId}/checklist`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    })
  }

  async updateChecklistItem(
    taskId: number,
    itemId: number,
    data: { text?: string; is_done?: boolean },
  ): Promise<ChecklistItem> {
    return request(`/work/tasks/${taskId}/checklist/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteChecklistItem(taskId: number, itemId: number): Promise<void> {
    return request(`/work/tasks/${taskId}/checklist/${itemId}`, { method: 'DELETE' })
  }

  // ── Labels registry — /api/work/labels ──────────────────────────────────
  async listLabels(): Promise<Label[]> {
    return request<Label[]>('/work/labels')
  }

  async createLabel(name: string, color?: string): Promise<Label> {
    return request<Label>('/work/labels', {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    })
  }

  async deleteLabel(id: number): Promise<void> {
    await request<void>(`/work/labels/${id}`, { method: 'DELETE' })
  }

  // ── Recurring tasks — /api/work/recurring-tasks ──────────────────────────
  async listRecurringTasks(): Promise<{ items: RecurringTask[] }> {
    return request('/work/recurring-tasks')
  }

  async createRecurringTask(data: Partial<RecurringTask>): Promise<RecurringTask> {
    return request('/work/recurring-tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateRecurringTask(id: number, data: Partial<RecurringTask>): Promise<RecurringTask> {
    return request(`/work/recurring-tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deactivateRecurringTask(id: number): Promise<void> {
    return request(`/work/recurring-tasks/${id}`, { method: 'DELETE' })
  }

  // ── Task templates — /api/work/task-templates ─────────────────────────────
  async snapshotTaskTemplate(taskId: number, name: string): Promise<TaskTemplate> {
    return tasksApi.snapshotTemplate(taskId, name)
  }

  async listTaskTemplates(): Promise<{ items: TaskTemplate[] }> {
    return request('/work/task-templates')
  }

  async instantiateTaskTemplate(
    templateId: number,
    opts: { project_id?: number; assigned_to?: number; due_date?: string },
  ): Promise<Task> {
    return request(`/work/task-templates/${templateId}/instantiate`, {
      method: 'POST',
      body: JSON.stringify(opts),
    })
  }

  // ── Per-task recurrence — /api/work/tasks/:id/recurrence ─────────────────
  async getTaskRecurrence(taskId: number): Promise<{ recurrence: RecurringTask | null }> {
    return tasksApi.getRecurrence(taskId)
  }

  async setTaskRecurrence(
    taskId: number,
    recurrenceType: 'daily' | 'weekly' | 'monthly' | 'yearly' | null,
  ): Promise<Task> {
    return tasksApi.setRecurrence(taskId, recurrenceType)
  }

  // ── Project templates — /api/work/project-templates ──────────────────────
  async listProjectTemplates(): Promise<{ items: ProjectTemplate[] }> {
    return request('/work/project-templates')
  }

  async instantiateProjectTemplate(
    templateId: number,
    opts: { name: string; description?: string; team_id?: number; start_date?: string },
  ): Promise<Project> {
    return request(`/work/project-templates/${templateId}/instantiate`, {
      method: 'POST',
      body: JSON.stringify(opts),
    })
  }

  async deleteProjectTemplate(templateId: number): Promise<void> {
    return request(`/work/project-templates/${templateId}`, { method: 'DELETE' })
  }

  // ── Project phases — /api/work/projects/:id/phases ──────────────────────────
  async listProjectPhases(projectId: number): Promise<{ items: ProjectPhase[] }> {
    return projectsApi.listPhases(projectId)
  }

  async createProjectPhase(projectId: number, data: Parameters<typeof projectsApi.createPhase>[1]): Promise<ProjectPhase> {
    return projectsApi.createPhase(projectId, data)
  }

  async updateProjectPhase(projectId: number, phaseId: number, data: Parameters<typeof projectsApi.updatePhase>[2]): Promise<ProjectPhase> {
    return projectsApi.updatePhase(projectId, phaseId, data)
  }

  async deleteProjectPhase(projectId: number, phaseId: number): Promise<void> {
    return projectsApi.deletePhase(projectId, phaseId)
  }

  // ── Task attachments — /api/work/tasks/:id/attachments ──────────────────
  async presignAttachment(taskId: number, file: { file_name: string; content_type: string; size_bytes: number }) {
    return request<{ upload_url: string; file_key: string }>(
      `/work/tasks/${taskId}/attachments/presign`, { method: 'POST', body: JSON.stringify(file) })
  }
  /**
   * PUT a file to a presigned upload URL.
   * Local-storage mode returns a same-origin app route (relative path) that is
   * `@tenant_required`, so it needs our Authorization header + cookies. Cloud
   * (R2/S3) returns an absolute presigned URL that self-authenticates via its
   * signature and must NOT receive our app auth headers.
   */
  async uploadToPresignedUrl(uploadUrl: string, file: File | Blob): Promise<void> {
    const isLocal = uploadUrl.startsWith('/')
    const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream' }
    if (isLocal) {
      const token = authStore.getState().accessToken
      if (token) headers['Authorization'] = `Bearer ${token}`
    }
    const res = await fetch(uploadUrl, {
      method: 'PUT', body: file, headers,
      ...(isLocal ? { credentials: 'include' as RequestCredentials } : {}),
    })
    if (!res.ok) throw new Error(`upload_failed_${res.status}`)
  }
  async confirmAttachment(taskId: number, meta: { file_key: string; file_name: string; content_type: string; size_bytes: number }) {
    return request<TaskAttachment>(`/work/tasks/${taskId}/attachments`,
      { method: 'POST', body: JSON.stringify(meta) })
  }
  async listAttachments(taskId: number) {
    return request<{ items: TaskAttachment[] }>(`/work/tasks/${taskId}/attachments`)
  }
  async deleteAttachment(id: number) {
    return request<void>(`/work/attachments/${id}`, { method: 'DELETE' })
  }
  attachmentDownloadUrl(id: number) { return `/api/work/attachments/${id}/download` }
  /**
   * Download an attachment to the user's device. The `/download` route is
   * `@tenant_required`, so a plain `<a href>` navigation (cookie-only, no Bearer
   * header) 401s. We fetch it authenticated, then trigger a client-side save.
   * Same-origin redirect (local mode → local-download) preserves the Authorization
   * header; a cross-origin redirect (cloud → presigned URL) drops it, which is
   * what the signed URL wants.
   */
  downloadAttachment(id: number, fileName: string): Promise<void> {
    return runAuthenticatedDownload(this.attachmentDownloadUrl(id), fileName)
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Helper functions
export const isAuthenticated = (): boolean => {
  return !!authStore.getState().accessToken
}

export const getToken = (): string | null => {
  return authStore.getState().accessToken
}

export const clearAuth = (): void => {
  authStore.getState().logout()
}
